import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Send, LayoutTemplate, AlertCircle } from "lucide-react";
import { toast } from "react-toastify";
import { getTemplates } from "../../apis/template";
import { sendTemplateMessage } from "../../apis/chat";
import SearchableSelect from "../ui/SearchableSelect";
import ThemeLoader from "../ui/ThemeLoader";
import TemplatePreview from "../templateCreator/TemplatePreview";
import { buildTemplatePreviewState } from "../../utils/campaignTemplatePreview";
import {
  extractRequiredParameters,
  getSampleForParam,
  buildUserVarsFromFormValues,
  validateTemplateVariableValues,
} from "../../utils/campaignTemplateUtils";

const filterApprovedTemplates = (rawTemplates) => {
  const approved = rawTemplates.filter(
    (t) => t.status === "approved" || t.status === "APPROVED"
  );
  const notAuth = approved.filter(
    (t) => (t.category || "").toLowerCase() !== "authentication"
  );
  const withQuality = notAuth.filter((t) => {
    const q = (t.quality_score || "").toString().toUpperCase();
    return q !== "RED";
  });
  return withQuality.length > 0 ? withQuality : notAuth;
};

const SendTemplateModal = ({
  isOpen,
  onClose,
  phoneNumberId,
  customerPhone,
  customerName,
  onSent,
}) => {
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [variableValues, setVariableValues] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [viewMode, setViewMode] = useState("edit");

  const selectedTemplate = useMemo(
    () =>
      templates.find(
        (t) => String(t.id).trim() === String(selectedTemplateId).trim()
      ) || null,
    [templates, selectedTemplateId]
  );

  const templateParams = useMemo(
    () => (selectedTemplate ? extractRequiredParameters(selectedTemplate) : []),
    [selectedTemplate]
  );

  const previewState = useMemo(() => {
    if (!selectedTemplate) return null;
    if (viewMode === "preview") {
      return buildTemplatePreviewState(selectedTemplate, variableValues);
    }
    return buildTemplatePreviewState(selectedTemplate, {});
  }, [selectedTemplate, variableValues, viewMode]);

  const isEditing = viewMode === "edit";

  const variableKeyResolver = useCallback(
    (component, varName) => {
      if (!selectedTemplate) return varName;
      const params = extractRequiredParameters(selectedTemplate);
      const found = params.find((p) => {
        if (component === "BUTTON") {
          return p.component === "BUTTON" && String(p.index) === String(varName);
        }
        if (p.component !== component) return false;
        if (p.paramName && p.paramName === varName) return true;
        if (p.varNum && String(p.varNum) === String(varName)) return true;
        if (p.key === varName) return true;
        return false;
      });
      if (found) return found.key;
      if (component === "HEADER") return `header_${varName}`;
      if (component === "BODY") return `body_${varName}`;
      if (component === "BUTTON") return `button_text_${varName}`;
      return varName;
    },
    [selectedTemplate]
  );

  const templateOptions = useMemo(
    () =>
      templates.map((t) => ({
        value: String(t.id),
        label: t.name,
        searchText: `${t.name} ${t.language || ""} ${t.category || ""}`.toLowerCase(),
      })),
    [templates]
  );

  const selectedTemplateOption = useMemo(
    () =>
      templateOptions.find((o) => o.value === String(selectedTemplateId)) || null,
    [templateOptions, selectedTemplateId]
  );

  const loadTemplates = useCallback(async () => {
    if (!phoneNumberId) return;
    setLoadingTemplates(true);
    try {
      const response = await getTemplates({
        limit: 1000,
        offset: 0,
        phoneId: phoneNumberId,
        status: "approved",
      });
      const raw =
        response.data.metaTemplates || response.data.templates || [];
      setTemplates(filterApprovedTemplates(raw));
    } catch (err) {
      console.error("Failed to load templates for live chat:", err);
      setTemplates([]);
      toast.error("Failed to load templates for this phone number");
    } finally {
      setLoadingTemplates(false);
    }
  }, [phoneNumberId]);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedTemplateId(null);
    setVariableValues({});
    setFieldErrors({});
    setSubmitError(null);
    setViewMode("edit");
    loadTemplates();
  }, [isOpen, loadTemplates]);

  useEffect(() => {
    if (!selectedTemplate) {
      setVariableValues({});
      setFieldErrors({});
      return;
    }
    const params = extractRequiredParameters(selectedTemplate);
    const next = {};
    params.forEach((param) => {
      next[param.key] = getSampleForParam(selectedTemplate, param) || "";
    });
    setVariableValues(next);
    setFieldErrors({});
    setSubmitError(null);
    setViewMode("edit");
  }, [selectedTemplate]);

  const handleVariableChange = (key, value) => {
    setVariableValues((prev) => ({ ...prev, [key]: value }));
    if (fieldErrors[key]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const handleSend = async () => {
    if (!selectedTemplate || !phoneNumberId || !customerPhone) return;

    const validation = validateTemplateVariableValues(
      selectedTemplate,
      variableValues
    );
    if (!validation.valid) {
      setFieldErrors(validation.errors);
      setSubmitError("Fill in all highlighted fields in the preview before sending.");
      setViewMode("edit");
      return;
    }

    setSubmitError(null);
    setSending(true);

    try {
      const userVars = buildUserVarsFromFormValues(
        selectedTemplate,
        variableValues
      );
      const response = await sendTemplateMessage(
        phoneNumberId,
        customerPhone,
        selectedTemplate,
        userVars
      );

      if (response.data?.success) {
        toast.success("Template sent successfully");
        onSent?.(response.data?.data?.message);
        onClose();
      } else {
        const msg =
          response.data?.message ||
          response.data?.errorMessage ||
          response.data?.error ||
          "Failed to send template";
        setSubmitError(msg);
        toast.error(msg);
      }
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Failed to send template";
      setSubmitError(msg);
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/50">
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.97 }}
          className="bg-[#e9eeea] rounded-2xl shadow-xl w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col border border-black/5"
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-black/8 bg-white flex items-center justify-between gap-3">
            <div className="min-w-0">
              <h2 className="text-sm font-bold text-[#17181b] flex items-center gap-2">
                <LayoutTemplate size={16} className="text-[#17181b]" />
                Send template
              </h2>
              <p className="text-xs text-gray-500 mt-0.5 truncate">
                To {customerName || customerPhone}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4">
            {/* Template picker */}
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1.5">
                Template
              </label>
              {loadingTemplates ? (
                <div className="flex items-center gap-2 py-3 text-sm text-gray-500">
                  <ThemeLoader size="xs" spinnerOnly />
                  Loading templates…
                </div>
              ) : (
                <SearchableSelect
                  options={templateOptions}
                  value={selectedTemplateOption}
                  onChange={(opt) => setSelectedTemplateId(opt?.value || null)}
                  placeholder="Search approved templates…"
                  isClearable
                  variant="campaign"
                  formatOptionLabel={(opt) => {
                    const tpl = templates.find(
                      (t) => String(t.id) === String(opt.value)
                    );
                    if (!tpl) return opt.label;
                    const meta = [tpl.language, tpl.category]
                      .filter(Boolean)
                      .join(" · ");
                    return (
                      <div>
                        <div className="font-semibold text-sm">{opt.label}</div>
                        {meta && (
                          <div className="text-[11px] text-gray-500">{meta}</div>
                        )}
                      </div>
                    );
                  }}
                />
              )}
              <p className="text-[11px] text-gray-500 mt-1.5">
                Approved templates for this WhatsApp number only.
              </p>
            </div>

            {/* Editable preview */}
            {previewState ? (
              <TemplatePreview
                state={previewState}
                editable={isEditing}
                variableValues={variableValues}
                variableErrors={fieldErrors}
                onVariableChange={handleVariableChange}
                variableKeyResolver={variableKeyResolver}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
              />
            ) : (
              <div className="min-h-[200px] flex items-center justify-center text-sm text-gray-500 bg-white rounded-xl border border-dashed border-gray-200">
                Select a template to preview and edit variables
              </div>
            )}

            {selectedTemplate && templateParams.length === 0 && (
              <p className="text-xs text-gray-600 text-center">
                This template has no variables — review the preview and send.
              </p>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-3 border-t border-black/8 bg-white flex flex-col sm:flex-row sm:items-center gap-3">
            {submitError && (
              <div className="flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-100 rounded-xl px-3 py-2 flex-1">
                <AlertCircle size={14} className="shrink-0 mt-0.5" />
                <span>{submitError}</span>
              </div>
            )}
            <div className="flex items-center gap-2 sm:ml-auto">
              <button
                type="button"
                onClick={onClose}
                disabled={sending}
                className="px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-100 rounded-xl transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSend}
                disabled={!selectedTemplate || sending}
                className="flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-bold text-[#17181b] bg-[#eefa94] hover:brightness-95 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl transition-all min-w-[140px]"
              >
                {sending ? (
                  <>
                    <ThemeLoader size="xs" spinnerOnly />
                    Sending…
                  </>
                ) : (
                  <>
                    <Send size={16} />
                    Send template
                  </>
                )}
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default SendTemplateModal;
